/**
 * Client-side image compression — resizes and re-encodes as JPEG before base64.
 * Keeps images under reasonable payload sizes for the LLM vision API.
 */

const DEFAULT_MAX_PX = 2048
const DEFAULT_QUALITY = 0.85

export async function compressImage(
  file: File,
  maxPx: number = DEFAULT_MAX_PX,
  quality: number = DEFAULT_QUALITY,
): Promise<{ base64: string; mimeType: string }> {
  const bitmap = await createImageBitmap(file)
  const { width, height } = bitmap

  let targetWidth = width
  let targetHeight = height

  if (width > maxPx || height > maxPx) {
    const ratio = Math.min(maxPx / width, maxPx / height)
    targetWidth = Math.round(width * ratio)
    targetHeight = Math.round(height * ratio)
  }

  const canvas = new OffscreenCanvas(targetWidth, targetHeight)
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight)
  bitmap.close()

  const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality })
  const buffer = await blob.arrayBuffer()
  const base64 = btoa(
    new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ''),
  )

  return { base64, mimeType: 'image/jpeg' }
}

/**
 * A capture whose single dominant colour covers at least this fraction of the
 * (downscaled) screenshot is treated as "mostly blank" — a failed/white render
 * or a near-empty page. Real landing pages with imagery sit well below this.
 */
export const CAPTURE_BLANK_THRESHOLD = 0.92

/**
 * measureBlankFraction — cheap "did the extraction work?" signal. Downscales the
 * captured PNG and returns the fraction of pixels matching the dominant
 * (background) colour. ~1.0 means a blank/failed render; a content-rich landing
 * page is far lower. Best-effort: callers should treat a throw as "unknown".
 */
export async function measureBlankFraction(base64Png: string): Promise<number> {
  const bitmap = await createImageBitmap(
    new Blob([base64ToBytes(base64Png) as BlobPart], { type: 'image/png' }),
  )
  const w = Math.min(80, bitmap.width)
  const h = Math.max(1, Math.round(bitmap.height * (w / bitmap.width)))
  const canvas = new OffscreenCanvas(w, h)
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!
  ctx.drawImage(bitmap, 0, 0, w, h)
  bitmap.close()

  const { data } = ctx.getImageData(0, 0, w, h)
  const total = w * h
  const counts = new Map<number, number>()
  for (let i = 0; i < data.length; i += 4) {
    // 5 bits/channel quantization buckets near-identical background pixels.
    const key = ((data[i] >> 3) << 10) | ((data[i + 1] >> 3) << 5) | (data[i + 2] >> 3)
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  let dominant = 0
  for (const n of counts.values()) if (n > dominant) dominant = n
  return total > 0 ? dominant / total : 1
}

function bytesToBase64(buffer: ArrayBuffer): string {
  // Chunked to stay O(n): per-byte string concat via reduce is O(n^2) and
  // stalls the UI on multi-MB captures.
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  return bytes
}

/**
 * Compress a (potentially very tall, multi-MB) landing-page screenshot into a
 * chat vision image under `maxBytes` (the twin chat API rejects images ≥ 4 MB).
 *
 * Unlike compressImage, this caps only the WIDTH — never the longest side — so a
 * tall full-page capture keeps a readable width instead of collapsing to a thin
 * strip. PNG → JPEG alone usually clears the budget; if a page is extreme, the
 * quality (then the scale) is stepped down until the encoded file fits.
 */
const CHAT_CAPTURE_MAX_WIDTH = 1280
const CHAT_CAPTURE_MAX_HEIGHT = 8192 // keep within canvas limits for very tall pages
// Budget on the encoded JPEG bytes. Base64 inflates size by ~4/3, so 3.8 MB raw
// is ~5.07 MB as a string — under the chat API's base64-length limit (5.5 MB).
const CHAT_IMAGE_MAX_BYTES = 3.8 * 1024 * 1024

export async function compressCaptureForChat(
  base64Png: string,
  maxBytes: number = CHAT_IMAGE_MAX_BYTES,
): Promise<{ base64: string; mimeType: string }> {
  const bitmap = await createImageBitmap(
    new Blob([base64ToBytes(base64Png) as BlobPart], { type: 'image/png' }),
  )

  const fit = (scale: number) => {
    let w = Math.max(1, Math.round(bitmap.width * scale))
    let h = Math.max(1, Math.round(bitmap.height * scale))
    if (w > CHAT_CAPTURE_MAX_WIDTH) {
      h = Math.max(1, Math.round((h * CHAT_CAPTURE_MAX_WIDTH) / w))
      w = CHAT_CAPTURE_MAX_WIDTH
    }
    if (h > CHAT_CAPTURE_MAX_HEIGHT) {
      w = Math.max(1, Math.round((w * CHAT_CAPTURE_MAX_HEIGHT) / h))
      h = CHAT_CAPTURE_MAX_HEIGHT
    }
    return { w, h }
  }

  const encode = async (scale: number, quality: number) => {
    const { w, h } = fit(scale)
    const canvas = new OffscreenCanvas(w, h)
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(bitmap, 0, 0, w, h)
    const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality })
    const buffer = await blob.arrayBuffer()
    return { base64: bytesToBase64(buffer), bytes: buffer.byteLength }
  }

  let scale = 1
  let quality = 0.82
  let out = await encode(scale, quality)
  // First trade quality, then resolution, until the encoded file fits.
  while (out.bytes > maxBytes && quality > 0.4) {
    quality -= 0.15
    out = await encode(scale, quality)
  }
  while (out.bytes > maxBytes && scale > 0.12) {
    scale *= 0.7
    out = await encode(scale, 0.55)
  }

  bitmap.close()
  return { base64: out.base64, mimeType: 'image/jpeg' }
}
